import {
  CartAction,
  CartActionType,
  CartItemType,
  OfferPayload,
} from "../../services/redux/types/CartLoyaltyV2Types";
import { AddonGroupSubItemType, SubItemType } from "../types";

type RawCustomPrice = {
  price: number;
  franchiseId?: number;
};

type CustomPrice = { price: number; franchiseId: number };

/**
 * Normalizes menu/ORM custom price rows to the legacy cart-calculate API shape
 * (`POST cart-loyalty/cart/calculate`).
 */
function sanitizeCustomFranchisePrices(
  prices: RawCustomPrice[] | undefined,
  franchiseId?: number
): CustomPrice[] {
  if (!prices?.length) {
    return [];
  }

  return prices.flatMap((entry) => {
    if (entry.franchiseId != null && entry.franchiseId > 0) {
      return [{ franchiseId: entry.franchiseId, price: entry.price }];
    }

    if (franchiseId != null && franchiseId > 0 && entry.price != null) {
      return [{ franchiseId, price: entry.price }];
    }

    return [];
  });
}

function sanitizeAddonGroupSubItem(
  addon: AddonGroupSubItemType,
  franchiseId?: number
): AddonGroupSubItemType {
  return {
    ...addon,
    customFranchiseAddonGroupSubItemPrices: sanitizeCustomFranchisePrices(
      addon.customFranchiseAddonGroupSubItemPrices,
      franchiseId
    ),
    nestedCustomFranchiseAddonGroupSubItemPrices: sanitizeCustomFranchisePrices(
      addon.nestedCustomFranchiseAddonGroupSubItemPrices,
      franchiseId
    ),
    subItem: addon.subItem
      ? sanitizeSubItemForCartCalculate(addon.subItem, franchiseId)
      : addon.subItem,
  };
}

function sanitizeSubItemForCartCalculate(
  subItem: SubItemType,
  franchiseId?: number
): SubItemType {
  const sanitized: SubItemType = {
    ...subItem,
    customFranchiseSubItemPrices: sanitizeCustomFranchisePrices(
      subItem.customFranchiseSubItemPrices,
      franchiseId
    ),
  };

  if (subItem.allAddonGroups?.length) {
    sanitized.allAddonGroups = subItem.allAddonGroups.map((group) => ({
      ...group,
      addonGroupSubItem:
        group.addonGroupSubItem?.map((addon) =>
          sanitizeAddonGroupSubItem(addon, franchiseId)
        ) ?? [],
    }));
  }

  return sanitized;
}

function sanitizeCartItemForCartCalculate(
  item: CartItemType,
  franchiseId?: number
): CartItemType {
  return {
    ...item,
    subItemDetails: sanitizeSubItemForCartCalculate(
      item.subItemDetails,
      franchiseId
    ),
  };
}

function sanitizeOfferPayload(
  offer: OfferPayload,
  franchiseId?: number
): OfferPayload {
  if ("item" in offer && offer.item) {
    return {
      ...offer,
      item: sanitizeCartItemForCartCalculate(offer.item, franchiseId),
      items: offer.items?.map((item) =>
        sanitizeCartItemForCartCalculate(item, franchiseId)
      ),
    };
  }

  if ("buyItem" in offer && offer.buyItem) {
    return {
      ...offer,
      buyItem: sanitizeCartItemForCartCalculate(offer.buyItem, franchiseId),
      getOffer: sanitizeOfferPayload(offer.getOffer, franchiseId),
    };
  }

  return offer;
}

/** Sanitizes legacy cartActions before POST cart-loyalty/cart/calculate. */
export function sanitizeCartActionsForCalculate(
  actions: CartAction[],
  franchiseId?: number
): CartAction[] {
  return actions.map((action) => {
    if (
      action.actionType === CartActionType.ADD_ITEM &&
      "item" in action &&
      action.item
    ) {
      return {
        ...action,
        item: sanitizeCartItemForCartCalculate(action.item, franchiseId),
      };
    }

    if (
      action.actionType === CartActionType.EDIT_ITEM &&
      "item" in action &&
      action.item
    ) {
      return {
        ...action,
        item: sanitizeCartItemForCartCalculate(action.item, franchiseId),
      };
    }

    if (
      action.actionType === CartActionType.REDEEM_OFFER &&
      "data" in action &&
      action.data
    ) {
      return {
        ...action,
        data: sanitizeOfferPayload(action.data, franchiseId),
      };
    }

    return action;
  });
}
